import { useCallback, useEffect, useRef, useState } from "react";
import { mergeRequestQueue } from "./requestQueueItems.ts";
import type { OperationHistoryDto } from "jadro-pico/dtos/pico_com/OperationHistoryDto.ts";
import { ApiPaths } from "jadro-pico/api-endpoints/ApiPaths.ts";
import type { ApiResponse } from "jadro-pico/dtos/common/ApiResponse.ts";
import type { PicoAppEnum } from "jadro-pico/apps/PicoAppEnum.ts";
import { useApiQuery } from "../Api/useApiQuery.ts";
import { OPERATION_HISTORY_QUERY_KEY } from "../Api/queryKeys.ts";
import {
  listAllMutations,
  subscribeToPendingMutations,
  type PendingMutation,
} from "./pendingMutationsDb.ts";

export function useOutbox(userUuid: string): PendingMutation[] {
  const [outbox, setOutbox] = useState<PendingMutation[]>([]);
  const readCounter = useRef(0);

  // A flush emits several changes per entry, so reads overlap; without this the view could settle
  // on whichever read happened to finish last rather than the newest one.
  const reload = useCallback(() => {
    const read = ++readCounter.current;
    void listAllMutations(userUuid).then((entries) => {
      if (read === readCounter.current) setOutbox(entries);
    });
  }, [userUuid]);

  useEffect(() => {
    reload();
    return subscribeToPendingMutations(reload);
  }, [reload]);

  return outbox;
}

// Only this app's rows: the hub in pico-com is what shows everything, and repeating it here would
// list operations the user cannot act on from this origin.
export function useRequestQueue(apiUrl: string, userUuid: string, app: PicoAppEnum) {
  const outbox = useOutbox(userUuid);

  const history = useApiQuery<ApiResponse<OperationHistoryDto[]>>({
    url: `${apiUrl}${ApiPaths.operations.prefix}?app=${app}`,
    queryKey: [...OPERATION_HISTORY_QUERY_KEY, app],
  });

  const items = mergeRequestQueue(outbox, history.data?.data ?? []);

  return { items, isLoading: history.isLoading, error: history.error };
}
